import CircleIcon from '@mui/icons-material/Circle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIconProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMainDrawer } from '../../../component/drawer/MainDrawerContext';

export interface BenefitMenuItemInterface {
  title: string | string[];
  to: string;
  hasSubpages?: boolean;
  // Optional regex to decide whether the menu item is active for the current route.
  activePattern?: string;
}

type BenefitMenuItemProps = {
  icon?: React.ComponentType<SvgIconProps>;
  title: string;
  active?: boolean;
  onClick: () => void;
  selected?: boolean;
  dataTestId?: string;
  badgeLabel?: string;
  subMenuItems?: BenefitMenuItemInterface[];
};

export const BenefitMenuItem: React.FC<BenefitMenuItemProps> = React.memo(
  ({ onClick, title, active, icon, selected, badgeLabel, dataTestId, subMenuItems }) => {
    const { t } = useTranslation('benefitsModule');
    let subMenu;
    if (subMenuItems?.length) {
      subMenu = subMenuItems.map(item => {
        const subMenuItemTitle = typeof item.title === 'string' ? t(item.title) : item.title.map(title => t(title)).join(': ');
        return (
          <SubMenuItem
            key={item.to}
            title={subMenuItemTitle}
            to={item.to}
            hasSubpages={item.hasSubpages}
            activePattern={item.activePattern}
          />
        );
      });
    }

    return (
      <MainMenuItem
        icon={icon}
        title={title}
        color={active ? undefined : 'disabled'}
        selected={selected}
        onClick={onClick}
        badgeLabel={badgeLabel}
        dataTestId={dataTestId}
        subMenu={active ? subMenu : undefined}
      />
    );
  },
);

export const MainMenuItem: React.FC<{
  icon?: React.ComponentType<Pick<SvgIconProps<'svg', {}>, 'color'>>;
  title: string;
  color?: SvgIconProps['color'];
  selected?: boolean;
  onClick: () => void;
  badgeLabel?: string;
  dataTestId?: string;
  className?: string;
  subMenu?: React.ReactNode;
}> = React.memo(({ icon, title, color, selected, badgeLabel, dataTestId, className, subMenu, onClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const { handleClose } = useMainDrawer();

  const handleClick = useCallback(() => {
    if (isMobile && !subMenu) {
      handleClose();
    }
    onClick();
  }, [handleClose, isMobile, onClick, subMenu]);

  const IconComponent = icon || MoreHorizIcon;

  return (
    <>
      <ListItem
        dense
        disablePadding
        className={className}
        aria-expanded={subMenu ? selected : undefined}
        aria-controls={subMenu ? 'sub-menu' : undefined}
      >
        <ListItemButton selected={selected} onClick={handleClick} data-test-id={dataTestId}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <IconComponent color={color || 'primary'} />
          </ListItemIcon>
          <ListItemText disableTypography>
            <Typography variant="body2" sx={{ color: `text.${color || 'secondary'}` }} component="span">
              {title}
            </Typography>
          </ListItemText>
          {badgeLabel && <Chip size="small" variant="outlined" color="error" label={badgeLabel} sx={{ pointerEvents: 'none' }} />}
          {subMenu &&
            !badgeLabel &&
            (selected ? <ExpandLessIcon color={color || 'primary'} /> : <ExpandMoreIcon color={color || 'primary'} />)}
        </ListItemButton>
      </ListItem>
      {subMenu && (
        <Collapse in={selected} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subMenu}
          </List>
        </Collapse>
      )}
    </>
  );
});

const SubMenuItem: React.FC<{ title: string; to: string; hasSubpages?: boolean; activePattern?: string }> = ({
  title,
  to,
  hasSubpages,
  activePattern,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const { handleClose } = useMainDrawer();

  const handleClick = useCallback(() => {
    if (isMobile) {
      handleClose();
    }
    navigate(to);
  }, [handleClose, isMobile, navigate, to]);

  const active = (() => {
    if (activePattern) {
      try {
        return new RegExp(activePattern).test(location.pathname);
      } catch {
        // ignore invalid regex and fall back to default matching
      }
    }

    return location.pathname === to || (hasSubpages && location.pathname.startsWith(`${to}/`));
  })();

  return (
    <ListItem dense disablePadding>
      <ListItemButton onClick={handleClick} data-test-id={`submenu${to.replaceAll('/', '-')}`}>
        <ListItemIcon sx={{ minWidth: 40 }}>
          <DotIcon large={active} />
        </ListItemIcon>
        <ListItemText
          slotProps={{
            primary: { sx: { color: 'text.secondary', fontWeight: active ? 'bold' : undefined } },
          }}
        >
          {title}
        </ListItemText>
      </ListItemButton>
    </ListItem>
  );
};

const DotIcon: React.FC<{ large?: boolean }> = ({ large }) => {
  return <CircleIcon color="primary" sx={{ fontSize: large ? '0.6em' : '0.3em', ml: large ? 0.8 : 1, transition: '0.1s' }} />;
};
